// src/pages/vendor/Products.jsx - COMPLETE ENHANCED WITH REAL-TIME SEARCH

import React, { useState, useEffect, useCallback } from 'react';
import { useVendor } from '../../hooks/useVendor';
import { useOptions } from '../../context/OptionsContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Rating,
  Divider,
  FormControl,
  InputLabel,
  Select,
  useMediaQuery,
  useTheme,
  Collapse,
  Tooltip,
  Pagination,
  Snackbar,
  OutlinedInput,
  Checkbox,
  ListItemText,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Zoom,
  Skeleton,
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Inventory,
  CheckCircle,
  Cancel,
  GridView,
  ViewList,
  Refresh,
  Warning as WarningIcon,
  Close,
  ExpandMore,
  ExpandLess,
  Image,
  CloudUpload,
  Delete as DeleteIcon,
  Store,
  Clear,
} from '@mui/icons-material';

// Constants
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// ============================================================
// DEBOUNCE HOOK
// ============================================================

const useDebounce = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.goscitech.com/api';
const API_DISPLAY_URL = API_BASE_URL.replace(/\/api$/, '');

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  if (imagePath.startsWith('blob:')) {
    return null;
  }
  const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
  return `${API_DISPLAY_URL}/${cleanPath}`;
};

const parseImages = (imagesData) => {
  if (!imagesData) return [];
  
  if (Array.isArray(imagesData)) {
    return imagesData.filter(img => img && img.trim() !== '' && !img.startsWith('blob:'));
  }
  
  if (typeof imagesData === 'string') {
    try {
      let parsed = JSON.parse(imagesData);
      
      if (typeof parsed === 'string') {
        try {
          const doubleParsed = JSON.parse(parsed);
          if (Array.isArray(doubleParsed)) {
            return doubleParsed.filter(img => img && img.trim() !== '' && !img.startsWith('blob:'));
          }
          if (typeof doubleParsed === 'string' && !doubleParsed.startsWith('blob:')) {
            return [doubleParsed];
          }
        } catch (e) {
          if (parsed.trim() !== '' && !parsed.startsWith('blob:')) {
            return [parsed];
          }
        }
      }
      
      if (Array.isArray(parsed)) {
        return parsed.filter(img => img && img.trim() !== '' && !img.startsWith('blob:'));
      }
      
      if (typeof parsed === 'string' && !parsed.startsWith('blob:')) {
        return [parsed];
      }
    } catch (e) {
      let cleaned = imagesData
        .replace(/^"|"$/g, '')
        .replace(/\\"/g, '"')
        .replace(/\\/g, '');
      
      if (cleaned.startsWith('[') || cleaned.startsWith('{')) {
        try {
          const parsedClean = JSON.parse(cleaned);
          if (Array.isArray(parsedClean)) {
            return parsedClean.filter(img => img && img.trim() !== '' && !img.startsWith('blob:'));
          }
          if (typeof parsedClean === 'string' && !parsedClean.startsWith('blob:')) {
            return [parsedClean];
          }
        } catch (e2) {
          if (cleaned.trim() !== '' && !cleaned.startsWith('blob:')) {
            return [cleaned];
          }
        }
      }
      
      if (imagesData.trim() !== '' && !imagesData.startsWith('blob:')) {
        return [imagesData];
      }
    }
  }
  
  return [];
};

const getProductThumbnail = (product) => {
  if (!product) return null;
  
  const images = parseImages(product.images);
  
  if (product.thumbnail) {
    if (typeof product.thumbnail === 'string') {
      if (product.thumbnail.startsWith('blob:')) {
        return images.length > 0 ? images[0] : null;
      }
      
      try {
        const parsed = JSON.parse(product.thumbnail);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const first = parsed[0];
          return first && !first.startsWith('blob:') ? first : null;
        }
        if (typeof parsed === 'string' && !parsed.startsWith('blob:')) {
          return parsed;
        }
      } catch (e) {
        let cleaned = product.thumbnail
          .replace(/^"|"$/g, '')
          .replace(/\\"/g, '"')
          .replace(/\\/g, '');
        
        if (cleaned.startsWith('[') || cleaned.startsWith('{')) {
          try {
            const parsedClean = JSON.parse(cleaned);
            if (Array.isArray(parsedClean) && parsedClean.length > 0) {
              const first = parsedClean[0];
              return first && !first.startsWith('blob:') ? first : null;
            }
            if (typeof parsedClean === 'string' && !parsedClean.startsWith('blob:')) {
              return parsedClean;
            }
          } catch (e2) {}
        }
        
        return cleaned.startsWith('blob:') ? null : cleaned;
      }
    }
    if (Array.isArray(product.thumbnail) && product.thumbnail.length > 0) {
      const first = product.thumbnail[0];
      return first && !first.startsWith('blob:') ? first : null;
    }
  }
  
  return images.length > 0 ? images[0] : null;
};

const formatCurrency = (amount) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
  return `ETB ${num.toFixed(2)}`;
};

// ============================================================
// IMAGE UPLOAD FUNCTION
// ============================================================

const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  
  try {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const uploadUrl = `${API_BASE_URL}/products/upload`;
    
    console.log('Uploading to:', uploadUrl);
    
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload response error:', response.status, errorText);
      throw new Error(`Upload failed with status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Upload response:', data);
    
    if (data.success) {
      return data.url;
    }
    return null;
  } catch (error) {
    console.error('Upload error:', error);
    return null;
  }
};

const VendorProducts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    products,
    loading: productsLoading,
    deleteProduct,
    fetchProducts: fetchVendorProducts,
    createProduct,
    updateProduct,
  } = useVendor();

  const { categories, sizes, colors, badges, loading: optionsLoading } = useOptions();

  // View state
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 6 : 10);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  
  // Image management
  const [productImages, setProductImages] = useState([]);
  const [thumbnailIndex, setThumbnailIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  
  // Product form state
  const [productFormData, setProductFormData] = useState({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    stock: '',
    categoryId: '',
    sizes: [],
    colors: [],
    badge: '',
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    lowStock: 0,
  });

  // ============================================================
  // DEBOUNCE SEARCH
  // ============================================================

  const debouncedSearch = useDebounce(searchTerm, 400);

  useEffect(() => {
    setDebouncedSearchTerm(debouncedSearch);
  }, [debouncedSearch]);

  // ============================================================
  // FETCH DATA
  // ============================================================
  const fetchProducts = useCallback(async () => {
    // Call the vendor hook's fetchProducts
    await fetchVendorProducts();
  }, [fetchVendorProducts]);

  // Auto-fetch when search term changes (debounced)
  useEffect(() => {
    fetchProducts();
  }, [debouncedSearchTerm, filterCategory, filterStatus, sortBy]);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (products) {
      setStats({
        total: products.length,
        active: products.filter(p => p.isActive).length,
        inactive: products.filter(p => !p.isActive).length,
        lowStock: products.filter(p => p.stock < 10 && p.stock > 0).length,
      });
    }
  }, [products]);

  // ============================================================
  // HANDLERS
  // ============================================================
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(0);
  };

  const handleFilterCategory = (category) => {
    setFilterCategory(category);
    setPage(0);
  };

  const handleFilterStatus = (status) => {
    setFilterStatus(status);
    setPage(0);
  };

  const handleSort = (e) => {
    setSortBy(e.target.value);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage - 1);
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductImages([]);
    setThumbnailIndex(0);
    setProductFormData({
      name: '',
      description: '',
      price: '',
      originalPrice: '',
      stock: '',
      categoryId: '',
      sizes: [],
      colors: [],
      badge: '',
    });
    setOpenDialog(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    
    const parsedImages = parseImages(product.images);
    const existingImages = parsedImages.map((img, idx) => ({
      id: `existing-${idx}`,
      url: img,
      isExisting: true,
      preview: getImageUrl(img),
      file: null,
    }));
    setProductImages(existingImages);
    
    const thumbnail = getProductThumbnail(product);
    setThumbnailIndex(thumbnail ? parsedImages.indexOf(thumbnail) : 0);
    
    setProductFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      originalPrice: product.originalPrice?.toString() || '',
      stock: product.stock?.toString() || '',
      categoryId: product.CategoryId || product.categoryId || '',
      sizes: product.sizes || [],
      colors: product.colors || [],
      badge: product.badge || '',
    });
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setViewDetailsDialog(true);
    handleMenuClose();
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;
    try {
      await deleteProduct(selectedProduct.id);
      showSnackbar(`Product "${selectedProduct.name}" deleted successfully`, 'success');
      setDeleteDialog(false);
      setSelectedProduct(null);
      await fetchProducts();
    } catch (error) {
      showSnackbar(error.message || 'Failed to delete product', 'error');
    }
  };

  const handleMenuOpen = (event, product) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpandProduct = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const handleFormChange = (field, value) => {
    setProductFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ============================================================
  // IMAGE HANDLERS
  // ============================================================
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map((file) => ({
      id: `new-${Date.now()}-${Math.random()}`,
      file: file,
      preview: URL.createObjectURL(file),
      isExisting: false,
    }));
    setProductImages((prev) => [...prev, ...newImages]);
    event.target.value = '';
  };

  const handleRemoveImage = (index) => {
    setProductImages((prev) => prev.filter((_, i) => i !== index));
    if (thumbnailIndex === index) {
      setThumbnailIndex(Math.min(0, productImages.length - 2));
    } else if (thumbnailIndex > index) {
      setThumbnailIndex(thumbnailIndex - 1);
    }
  };

  const handleSetThumbnail = (index) => {
    setThumbnailIndex(index);
  };

  // ============================================================
  // SAVE PRODUCT
  // ============================================================
  const handleSaveProduct = async () => {
    try {
      if (!productFormData.name.trim()) {
        showSnackbar('Product name is required', 'error');
        return;
      }
      if (!productFormData.price || parseFloat(productFormData.price) <= 0) {
        showSnackbar('Valid price is required', 'error');
        return;
      }
      if (!productFormData.stock || parseInt(productFormData.stock) < 0) {
        showSnackbar('Valid stock quantity is required', 'error');
        return;
      }
      if (!productFormData.categoryId) {
        showSnackbar('Category is required', 'error');
        return;
      }

      setUploading(true);
      
      const imageUrlsList = [];
      let thumbnailUrl = '';

      for (let i = 0; i < productImages.length; i++) {
        const img = productImages[i];
        if (img.isExisting) {
          if (img.url && !img.url.startsWith('blob:')) {
            imageUrlsList.push(img.url);
          }
        } else if (img.file) {
          const uploadedUrl = await uploadImage(img.file);
          if (uploadedUrl) {
            imageUrlsList.push(uploadedUrl);
            if (i === thumbnailIndex) {
              thumbnailUrl = uploadedUrl;
            }
          } else {
            showSnackbar('Failed to upload image', 'error');
            setUploading(false);
            return;
          }
        }
      }

      if (!thumbnailUrl && imageUrlsList.length > 0) {
        thumbnailUrl = imageUrlsList[0];
      }

      const data = {
        name: productFormData.name.trim(),
        description: productFormData.description.trim(),
        price: parseFloat(productFormData.price),
        originalPrice: productFormData.originalPrice ? parseFloat(productFormData.originalPrice) : null,
        stock: parseInt(productFormData.stock),
        CategoryId: parseInt(productFormData.categoryId),
        sizes: productFormData.sizes,
        colors: productFormData.colors,
        badge: productFormData.badge || null,
        images: imageUrlsList,
        thumbnail: thumbnailUrl,
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, data);
        showSnackbar('Product updated successfully!', 'success');
      } else {
        await createProduct(data);
        showSnackbar('Product created successfully!', 'success');
      }
      setOpenDialog(false);
      setUploading(false);
      await fetchProducts();
    } catch (error) {
      console.error('Save error:', error);
      setUploading(false);
      showSnackbar(error.message || 'Failed to save product', 'error');
    }
  };

  const handleRefresh = async () => {
    await fetchProducts();
    showSnackbar('Products refreshed', 'info');
  };

  // ============================================================
  // FILTERING & SORTING (client-side for vendor)
  // ============================================================
  const filteredProducts = products?.filter((product) => {
    const matchesSearch = product.name
      .toLowerCase()
      .includes(debouncedSearchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.Category?.id === filterCategory || product.category === filterCategory;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && product.isActive) ||
      (filterStatus === 'inactive' && !product.isActive);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const sortedProducts = [...(filteredProducts || [])].sort((a, b) => {
    switch (sortBy) {
      case 'newest': return new Date(b.createdAt) - new Date(a.createdAt);
      case 'oldest': return new Date(a.createdAt) - new Date(b.createdAt);
      case 'price_asc': return a.price - b.price;
      case 'price_desc': return b.price - a.price;
      case 'name_asc': return a.name.localeCompare(b.name);
      case 'name_desc': return b.name.localeCompare(a.name);
      case 'stock_asc': return a.stock - b.stock;
      case 'stock_desc': return b.stock - a.stock;
      default: return 0;
    }
  });

  const paginatedProducts = sortedProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalPages = Math.ceil(sortedProducts.length / rowsPerPage);

  // ============================================================
  // RENDER HELPERS
  // ============================================================
  const getStockColor = (stock) => {
    if (stock > 10) return 'success';
    if (stock > 0) return 'warning';
    return 'error';
  };

  const getStockLabel = (stock) => {
    if (stock > 10) return 'In Stock';
    if (stock > 0) return 'Low Stock';
    return 'Out of Stock';
  };

  // ============================================================
  // RENDER: Mobile Product Card
  // ============================================================
  const renderMobileProductCard = (product) => {
    const isExpanded = expandedProduct === product.id;
    const images = parseImages(product.images);
    const thumbnail = getProductThumbnail(product);

    return (
      <Card key={product.id} sx={{ mb: 2, borderRadius: 2, position: 'relative' }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2}>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar
                  variant="rounded"
                  src={getImageUrl(thumbnail)}
                  sx={{ width: 80, height: 80, borderRadius: 2 }}
                >
                  <Inventory />
                </Avatar>
                {product.badge && (
                  <Chip
                    label={product.badge}
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -4,
                      left: -4,
                      height: 18,
                      fontSize: '0.5rem',
                    }}
                  />
                )}
                {images.length > 1 && (
                  <Chip
                    label={`${images.length}`}
                    size="small"
                    icon={<Image sx={{ fontSize: 12 }} />}
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      right: -4,
                      height: 18,
                      fontSize: '0.5rem',
                      bgcolor: 'primary.main',
                      color: 'white',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {product.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {product.Category?.name || 'No Category'}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {formatCurrency(product.price)}
                  </Typography>
                  {product.originalPrice && (
                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      {formatCurrency(product.originalPrice)}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Rating value={product.rating || 0} size="small" readOnly />
                  <Typography variant="caption" color="text.secondary">
                    ({product.reviewsCount || 0})
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                icon={product.isActive ? <CheckCircle /> : <Cancel />}
                label={product.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={product.isActive ? 'success' : 'error'}
                sx={{ height: 24 }}
              />
              <Chip
                label={`Stock: ${product.stock || 0}`}
                size="small"
                color={getStockColor(product.stock || 0)}
                variant="outlined"
                sx={{ height: 24 }}
              />
              {product.stock < 10 && product.stock > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label="Low Stock"
                  size="small"
                  color="warning"
                  sx={{ height: 24 }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => {
                  setSelectedProduct(product);
                  setViewDetailsDialog(true);
                }}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
              >
                View
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => handleEditProduct(product)}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
              >
                Edit
              </Button>
              <IconButton size="small" onClick={() => handleExpandProduct(product.id)}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>

            <Collapse in={isExpanded}>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={1.5}>
                {images.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Images ({images.length})
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, overflow: 'auto', pb: 0.5 }}>
                      {images.map((img, idx) => (
                        <Avatar
                          key={idx}
                          variant="rounded"
                          src={getImageUrl(img)}
                          sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: 1,
                            border: thumbnail === img || (idx === 0 && !thumbnail) 
                              ? '2px solid #1976d2' 
                              : 'none',
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {product.sizes?.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Sizes
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                      {product.sizes.map((size) => (
                        <Chip key={size} label={size} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                )}
                
                {product.colors?.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Colors
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                      {product.colors.map((color) => (
                        <Chip
                          key={color}
                          label={color}
                          size="small"
                          sx={{ bgcolor: color.toLowerCase(), color: '#fff' }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {product.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {product.description.length > 100 
                        ? `${product.description.substring(0, 100)}...` 
                        : product.description}
                    </Typography>
                  </Box>
                )}

                <Button
                  size="small"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => {
                    setSelectedProduct(product);
                    setDeleteDialog(true);
                  }}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                >
                  Delete Product
                </Button>
              </Stack>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // ============================================================
  // RENDER: Grid View
  // ============================================================
  const renderGridView = () => (
    <Grid container spacing={isMobile ? 1.5 : 3}>
      {paginatedProducts.map((product) => {
        const images = parseImages(product.images);
        const thumbnail = getProductThumbnail(product);
        
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
            <Zoom in style={{ transitionDelay: '100ms' }}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: theme.shadows[10],
                },
              }}>
                <Box sx={{ position: 'relative', pt: '100%', bgcolor: 'grey.100' }}>
                  <CardMedia
                    component="img"
                    image={getImageUrl(thumbnail) || '/placeholder-image.png'}
                    alt={product.name}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  {product.badge && (
                    <Chip
                      label={product.badge}
                      size="small"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        fontWeight: 'bold',
                        height: 24,
                        fontSize: '0.65rem',
                      }}
                    />
                  )}
                  {images.length > 1 && (
                    <Chip
                      label={`${images.length} images`}
                      size="small"
                      icon={<Image sx={{ fontSize: 14 }} />}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        height: 24,
                        fontSize: '0.6rem',
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                      }}
                    />
                  )}
                  <Chip
                    label={getStockLabel(product.stock || 0)}
                    size="small"
                    color={getStockColor(product.stock || 0)}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      height: 24,
                      fontSize: '0.65rem',
                    }}
                  />
                  {!product.isActive && (
                    <Chip
                      label="Inactive"
                      size="small"
                      color="default"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        height: 24,
                        fontSize: '0.65rem',
                      }}
                    />
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="600" noWrap>
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {product.Category?.name || 'No Category'}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {formatCurrency(product.price)}
                    </Typography>
                    {product.originalPrice && (
                      <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                        {formatCurrency(product.originalPrice)}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                    <Rating value={product.rating || 0} size="small" readOnly />
                    <Typography variant="caption" color="text.secondary">
                      ({product.reviewsCount || 0})
                    </Typography>
                  </Stack>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', p: 2, pt: 0 }}>
                  <Chip
                    icon={product.isActive ? <CheckCircle /> : <Cancel />}
                    label={product.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={product.isActive ? 'success' : 'error'}
                    sx={{ height: 24, fontSize: '0.65rem' }}
                  />
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditProduct(product)}
                        sx={{ '&:hover': { color: 'primary.main' } }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, product)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Stack>
                </CardActions>
              </Card>
            </Zoom>
          </Grid>
        );
      })}
    </Grid>
  );

  // ============================================================
  // RENDER: List View
  // ============================================================
  const renderListView = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Table>
        <TableHead sx={{ bgcolor: 'grey.50' }}>
          <TableRow>
            <TableCell>Product</TableCell>
            <TableCell>Category</TableCell>
            <TableCell align="right">Price</TableCell>
            <TableCell align="right">Stock</TableCell>
            <TableCell>Images</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedProducts.map((product) => {
            const images = parseImages(product.images);
            const thumbnail = getProductThumbnail(product);
            
            return (
              <TableRow key={product.id} hover>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      variant="rounded"
                      src={getImageUrl(thumbnail)}
                      sx={{ width: 48, height: 48 }}
                    >
                      <Inventory />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        {product.name}
                      </Typography>
                      {product.badge && (
                        <Chip
                          label={product.badge}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mt: 0.5, height: 20, fontSize: '0.6rem' }}
                        />
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>{product.Category?.name || 'N/A'}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="600" color="primary">
                    {formatCurrency(product.price)}
                  </Typography>
                  {product.originalPrice && (
                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      {formatCurrency(product.originalPrice)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={product.stock || 0}
                    size="small"
                    color={getStockColor(product.stock || 0)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      icon={<Image sx={{ fontSize: 14 }} />}
                      label={images.length}
                      size="small"
                      variant="outlined"
                    />
                    {images.length > 0 && (
                      <Stack direction="row" spacing={0.5}>
                        {images.slice(0, 3).map((img, idx) => (
                          <Avatar
                            key={idx}
                            src={getImageUrl(img)}
                            sx={{ width: 24, height: 24, borderRadius: 1 }}
                          />
                        ))}
                        {images.length > 3 && (
                          <Chip label={`+${images.length - 3}`} size="small" variant="outlined" />
                        )}
                      </Stack>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={product.isActive ? <CheckCircle /> : <Cancel />}
                    label={product.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={product.isActive ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, product)}
                  >
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ============================================================
  // RENDER: Loading
  // ============================================================
  if (productsLoading || optionsLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Box>
    );
  }

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Products
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your product inventory ({stats.total} total)
            </Typography>
          </Box>
          <Stack 
            direction="row" 
            spacing={1} 
            width={{ xs: '100%', sm: 'auto' }}
            flexWrap="wrap"
          >
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddProduct}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              Add Product
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Inventory color="primary" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                {stats.total}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Products
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <CheckCircle color="success" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="success.main">
                {stats.active}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Cancel color="error" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="error.main">
                {stats.inactive}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Inactive
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <WarningIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: 'warning.main' }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="warning.main">
                {stats.lowStock}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Low Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Toolbar - Enhanced with Real-time Search */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={isMobile ? 1 : 2} 
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            placeholder="Search products by name or description..."
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack 
            direction="row" 
            spacing={1} 
            flexWrap="wrap"
            sx={{ gap: { xs: 0.5, sm: 1 } }}
          >
            <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => handleFilterCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 80, sm: 100 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => handleFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
              <InputLabel>Sort</InputLabel>
              <Select
                value={sortBy}
                onChange={handleSort}
                label="Sort"
              >
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
                <MenuItem value="price_asc">Price: Low to High</MenuItem>
                <MenuItem value="price_desc">Price: High to Low</MenuItem>
                <MenuItem value="name_asc">Name: A to Z</MenuItem>
                <MenuItem value="name_desc">Name: Z to A</MenuItem>
                <MenuItem value="stock_asc">Stock: Low to High</MenuItem>
                <MenuItem value="stock_desc">Stock: High to Low</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Grid View">
                <IconButton
                  onClick={() => setViewMode('grid')}
                  color={viewMode === 'grid' ? 'primary' : 'default'}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <GridView />
                </IconButton>
              </Tooltip>
              <Tooltip title="List View">
                <IconButton
                  onClick={() => setViewMode('list')}
                  color={viewMode === 'list' ? 'primary' : 'default'}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <ViewList />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {/* Content */}
      {sortedProducts.length === 0 ? (
        <Paper sx={{ p: { xs: 4, sm: 6 }, borderRadius: 3, textAlign: 'center' }}>
          <Inventory sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm || filterCategory !== 'all' || filterStatus !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Start adding products to your store'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddProduct}
          >
            Add Product
          </Button>
        </Paper>
      ) : (
        <>
          {isMobile ? (
            <Box>
              {paginatedProducts.map((product) => renderMobileProductCard(product))}
            </Box>
          ) : (
            viewMode === 'grid' ? renderGridView() : renderListView()
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 180 }
        }}
      >
        <MenuItem onClick={() => handleViewProduct(selectedProduct)}>
          <Visibility fontSize="small" sx={{ mr: 1.5 }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => handleEditProduct(selectedProduct)}>
          <Edit fontSize="small" sx={{ mr: 1.5 }} /> Edit
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1.5 }} /> Delete
        </MenuItem>
      </Menu>

      {/* ============================================================ */}
      {/* ADD/EDIT PRODUCT DIALOG - NO VENDOR SELECTION */}
      {/* ============================================================ */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 }, maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Product Name *"
              value={productFormData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={productFormData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              multiline
              rows={3}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Price *"
                  type="number"
                  value={productFormData.price}
                  onChange={(e) => handleFormChange('price', e.target.value)}
                  required
                  InputProps={{ 
                    startAdornment: <InputAdornment position="start">ETB</InputAdornment>,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Original Price"
                  type="number"
                  value={productFormData.originalPrice}
                  onChange={(e) => handleFormChange('originalPrice', e.target.value)}
                  InputProps={{ 
                    startAdornment: <InputAdornment position="start">ETB</InputAdornment>,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Stock *"
                  type="number"
                  value={productFormData.stock}
                  onChange={(e) => handleFormChange('stock', e.target.value)}
                  required
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category *</InputLabel>
                  <Select
                    value={productFormData.categoryId}
                    onChange={(e) => handleFormChange('categoryId', e.target.value)}
                    label="Category *"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Sizes */}
            <FormControl fullWidth>
              <InputLabel>Sizes</InputLabel>
              <Select
                multiple
                value={productFormData.sizes}
                onChange={(e) => handleFormChange('sizes', e.target.value)}
                input={<OutlinedInput label="Sizes" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {sizes.map((size) => {
                  const sizeName = size.name || size;
                  return (
                    <MenuItem key={size.id || sizeName} value={sizeName}>
                      <Checkbox checked={productFormData.sizes.indexOf(sizeName) > -1} />
                      <ListItemText primary={sizeName} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Colors */}
            <FormControl fullWidth>
              <InputLabel>Colors</InputLabel>
              <Select
                multiple
                value={productFormData.colors}
                onChange={(e) => handleFormChange('colors', e.target.value)}
                input={<OutlinedInput label="Colors" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{ bgcolor: value.toLowerCase(), color: '#fff' }}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {colors.map((color) => {
                  const colorName = color.name || color;
                  return (
                    <MenuItem key={color.id || colorName} value={colorName}>
                      <Checkbox checked={productFormData.colors.indexOf(colorName) > -1} />
                      <ListItemText primary={colorName} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Badge */}
            <FormControl fullWidth>
              <InputLabel>Badge (Optional)</InputLabel>
              <Select
                value={productFormData.badge}
                onChange={(e) => handleFormChange('badge', e.target.value)}
                label="Badge (Optional)"
              >
                <MenuItem value="">None</MenuItem>
                {badges.map((badge) => {
                  const badgeName = badge.name || badge;
                  return (
                    <MenuItem key={badge.id || badgeName} value={badgeName}>
                      {badgeName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Image Upload Section */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                Product Images
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Upload multiple images for your product. Click an image to set it as the thumbnail.
              </Typography>

              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                sx={{ mb: 2 }}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Images'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
              </Button>

              {uploading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Uploading images...
                  </Typography>
                </Box>
              )}

              {productImages.length > 0 ? (
                <ImageList cols={isMobile ? 3 : 4} gap={8} sx={{ mb: 2 }}>
                  {productImages.map((img, index) => (
                    <ImageListItem
                      key={img.id}
                      sx={{
                        cursor: 'pointer',
                        border: thumbnailIndex === index ? '3px solid #1976d2' : '3px solid transparent',
                        borderRadius: 1,
                        overflow: 'hidden',
                        position: 'relative',
                        '&:hover .image-actions': { opacity: 1 },
                      }}
                      onClick={() => !uploading && handleSetThumbnail(index)}
                    >
                      <img
                        src={img.preview || img.url}
                        alt={`Product image ${index + 1}`}
                        loading="lazy"
                        style={{ width: '100%', height: 100, objectFit: 'cover' }}
                      />
                      {thumbnailIndex === index && (
                        <Chip
                          label="Thumbnail"
                          size="small"
                          color="primary"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            height: 20,
                            fontSize: '0.55rem',
                          }}
                        />
                      )}
                      <Box
                        className="image-actions"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          display: 'flex',
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          color="error"
                          sx={{ bgcolor: 'rgba(255,255,255,0.8)' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!uploading) handleRemoveImage(index);
                          }}
                          disabled={uploading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <ImageListItemBar
                        subtitle={`Image ${index + 1}`}
                        position="below"
                        sx={{ 
                          '& .MuiImageListItemBar-subtitle': { fontSize: '0.6rem', textAlign: 'center' },
                          bgcolor: 'transparent',
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              ) : (
                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    bgcolor: '#f9f9f9',
                  }}
                >
                  <Image sx={{ fontSize: 48, color: '#aaa' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No images uploaded yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click "Upload Images" to add product photos
                  </Typography>
                </Box>
              )}

              {productImages.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  * Click an image to set it as the thumbnail (highlighted in blue).
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenDialog(false)} fullWidth={isMobile} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveProduct}
            fullWidth={isMobile}
            disabled={!productFormData.name || !productFormData.price || !productFormData.categoryId || uploading}
          >
            {uploading ? 'Saving...' : (editingProduct ? 'Update Product' : 'Create Product')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDetailsDialog}
        onClose={() => setViewDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h6" fontWeight="bold">
              {selectedProduct?.name}
            </Typography>
            <Chip
              icon={selectedProduct?.isActive ? <CheckCircle /> : <Cancel />}
              label={selectedProduct?.isActive ? 'Active' : 'Inactive'}
              color={selectedProduct?.isActive ? 'success' : 'error'}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <Stack spacing={3}>
              {parseImages(selectedProduct.images).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                    Images ({parseImages(selectedProduct.images).length})
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ overflow: 'auto', pb: 1 }}>
                    {parseImages(selectedProduct.images).map((image, index) => {
                      const thumbnail = getProductThumbnail(selectedProduct);
                      return (
                        <Box
                          key={index}
                          sx={{
                            position: 'relative',
                            border: thumbnail === image || (index === 0 && !thumbnail)
                              ? '2px solid #1976d2'
                              : '2px solid transparent',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Avatar
                            variant="rounded"
                            src={getImageUrl(image)}
                            sx={{ width: 100, height: 100, borderRadius: 1 }}
                          />
                          {(thumbnail === image || (index === 0 && !thumbnail)) && (
                            <Chip
                              label="Thumbnail"
                              size="small"
                              color="primary"
                              sx={{
                                position: 'absolute',
                                bottom: 4,
                                left: 4,
                                height: 18,
                                fontSize: '0.5rem',
                              }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              right: 4,
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              px: 0.5,
                              borderRadius: 0.5,
                              fontSize: '0.6rem',
                            }}
                          >
                            #{index + 1}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Category
                  </Typography>
                  <Typography variant="body2">{selectedProduct.Category?.name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Price
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {formatCurrency(selectedProduct.price)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Stock
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {selectedProduct.stock || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Rating
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Rating value={selectedProduct.rating || 0} readOnly />
                    <Typography variant="body2">
                      ({selectedProduct.reviewsCount || 0} reviews)
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider />

              {selectedProduct.sizes?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Sizes
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    {selectedProduct.sizes.map((size) => (
                      <Chip key={size} label={size} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}

              {selectedProduct.colors?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Colors
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    {selectedProduct.colors.map((color) => (
                      <Chip
                        key={color}
                        label={color}
                        size="small"
                        sx={{ bgcolor: color.toLowerCase(), color: '#fff' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Description
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedProduct.description || 'No description provided'}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setViewDetailsDialog(false)} fullWidth={isMobile}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => {
              setViewDetailsDialog(false);
              handleEditProduct(selectedProduct);
            }}
            fullWidth={isMobile}
          >
            Edit Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1, verticalAlign: 'middle' }} /> Delete Product
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{selectedProduct?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setDeleteDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteProduct}
            color="error"
            variant="contained"
            fullWidth={isMobile}
            startIcon={<Delete />}
          >
            Delete Product
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorProducts;